import React from 'react';

import { FormatUtils, VehicleComponentSpeedModeInfo } from '@dndbeyond/character-rules-engine/es';

import { GD_VehicleBlockPrimaryComponentProps } from '../../../utils/Component';
import InlineSeparatedNodes from '../../common/InlineSeparatedNodes';
import { VehicleBlockAttribute } from '../VehicleBlockAttribute';

interface Props extends GD_VehicleBlockPrimaryComponentProps {
    shouldCoalesce: boolean;
    coverType: string | null;
    requiredCrew?: number | null;
    count: number;
}
export default class VehicleBlockPrimaryAttributes extends React.PureComponent<Props> {
    //this class renders on VehicleBlock and VehicleComponent and VehicleActionStation
    static defaultProps = {
        shouldCoalesce: true,
        coverType: null,
        requiredCrew: null,
        count: 1,
    };

    renderHitPointInfo = (): React.ReactNode => {
        const { hitPointInfo, count, shouldCoalesce } = this.props;

        if (hitPointInfo === null || hitPointInfo.totalHp === null) {
            return null;
        }

        let currentHpNode: React.ReactNode = null;
        if (shouldCoalesce) {
            currentHpNode = hitPointInfo.totalHp;
            if (count > 1) {
                currentHpNode = `${hitPointInfo.totalHp} each`;
            }
        } else {
            let classNames: Array<string> = ['ct-vehicle-block-component__hp-value'];

            const remainingHp: number =
                hitPointInfo.remainingHp !== null ? hitPointInfo.remainingHp : hitPointInfo.totalHp;
            if (remainingHp < hitPointInfo.totalHp) {
                classNames.push('ct-vehicle-block-component__hp-value--is-damaged');
            }

            let showFullHitPointInfo: boolean = !shouldCoalesce;

            currentHpNode = (
                <React.Fragment>
                    {showFullHitPointInfo && (
                        <React.Fragment>
                            <span className={classNames.join(' ')}>{remainingHp}</span>
                            <span className="ct-vehicle-block-component__hp-sep">/</span>
                        </React.Fragment>
                    )}
                    <span className={classNames.join(' ')}>{hitPointInfo.totalHp}</span>
                </React.Fragment>
            );
        }

        let displayNodes: Array<React.ReactNode> = [currentHpNode];

        if (hitPointInfo.hitPointSpeedAdjustments.length > 1) {
            hitPointInfo.hitPointSpeedAdjustments.forEach((adjustment) => {
                let speed: string = FormatUtils.renderDistance(adjustment.perDamageValue);
                displayNodes.push(`${speed} speed per ${adjustment.perDamageTaken} damage taken`);
            });
        }

        return <InlineSeparatedNodes nodes={displayNodes} sep={'; '} />;
    };

    renderHitPointThresholdInfo = (): React.ReactNode => {
        const { hitPointInfo } = this.props;

        if (hitPointInfo === null) {
            return null;
        }

        let displayStrings: Array<string> = [];

        if (hitPointInfo.damageThreshold) {
            displayStrings.push(`damage threshold ${hitPointInfo.damageThreshold}`);
        }

        if (hitPointInfo.mishapThreshold) {
            displayStrings.push(`mishap threshold ${hitPointInfo.mishapThreshold}`);
        }

        if (displayStrings.length === 0) {
            return null;
        }

        return `(${displayStrings.join(', ')})`;
    };

    renderSpeedInfo = (modes: Array<VehicleComponentSpeedModeInfo>): React.ReactNode => {
        let speedDisplayStrings: Array<string> = modes.map((mode) => {
            const { value, description, restrictionsText, movementInfo } = mode;

            let stringParts: Array<string> = [];
            if (movementInfo) {
                stringParts.push(`${movementInfo.description} speed`);
            }
            stringParts.push(FormatUtils.renderDistance(value));
            if (description) {
                stringParts.push(description);
            }
            if (restrictionsText) {
                stringParts.push(`(${restrictionsText})`);
            }

            return stringParts.join(' ');
        });

        return <InlineSeparatedNodes nodes={speedDisplayStrings} sep={'; '} />;
    };

    renderArmorClassAttribute = (): React.ReactNode => {
        const { armorClassInfo } = this.props;

        if (armorClassInfo === null) {
            return null;
        }

        const { moving, base } = armorClassInfo;

        let armorClassValue: number | null = base;
        let motionlessArmorClassValue: string | null = null;

        if (moving !== null) {
            armorClassValue = moving;

            if (moving !== base) {
                motionlessArmorClassValue = `(${base} while motionless)`;
            }
        }

        return (
            <VehicleBlockAttribute label="Armor Class" value={armorClassValue} extraValue={motionlessArmorClassValue} />
        );
    };

    render() {
        const { armorClassInfo, hitPointInfo, speedInfos, coverType, requiredCrew } = this.props;

        return (
            <React.Fragment>
                {armorClassInfo !== null && this.renderArmorClassAttribute()}
                {hitPointInfo !== null && (
                    <VehicleBlockAttribute
                        label="Hit Points"
                        value={this.renderHitPointInfo()}
                        extraValue={this.renderHitPointThresholdInfo()}
                    />
                )}
                {speedInfos.map((speedInfo, idx) => {
                    let speedAttributeLabel: string = 'Speed';
                    if (speedInfo.type !== null) {
                        speedAttributeLabel = `Speed (${speedInfo.type})`;
                    }

                    let modes: Array<VehicleComponentSpeedModeInfo> = speedInfo.modes !== null ? speedInfo.modes : [];

                    return (
                        <VehicleBlockAttribute
                            key={`${speedInfo.type}-${idx}`}
                            label={speedAttributeLabel}
                            value={this.renderSpeedInfo(modes)}
                        />
                    );
                })}
                {coverType !== null && <VehicleBlockAttribute label="Cover" value={`Grants ${coverType} Cover`} />}
                {requiredCrew !== null && <VehicleBlockAttribute label="Required Crew" value={requiredCrew} />}
            </React.Fragment>
        );
    }
}
